import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Nav, NavLink, NavItem, TabContent, TabPane, Pagination, PaginationItem, PaginationLink } from '@/components/ui-compat'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  List,
  Package as PackageIcon,
  FileText,
  Plus,
  Settings,
} from 'lucide-react'

import * as Query from '../../api/gql/query'
import { useModuleActions } from '../../hooks/useMyPermissions'
import { useRouter, useTabSwitch } from '../../hooks'
import { useDebounce } from '../../hooks/useDebounce'

import SubmittalsListSkeleton from './components/SubmittalsListSkeleton'
import SubmittalsFilterBar, { EMPTY_SUBMITTALS_FILTERS, SubmittalsFilters } from './components/SubmittalsFilterBar'
import SubmittalsList from './components/SubmittalsList'
import SubmittalCreateDialog from './components/SubmittalCreateDialog'
import SubmittalLookupsDialog from './components/SubmittalLookupsDialog'

const DASH = '—'

/** Client-side grouping of the already-fetched page by a lookup field's display value. */
const groupByLookup = (items: any[], field: 'package' | 'specSection'): [string, any[]][] => {
  const map = new Map<string, any[]>()
  items.forEach((item) => {
    const key = item[field]?.value || DASH
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(item)
  })
  return [...map.entries()].sort(([a], [b]) => {
    if (a === DASH) return 1
    if (b === DASH) return -1
    return a.localeCompare(b)
  })
}

const Submittals: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const { canCreate, canAdmin } = useModuleActions('SUBMITTALS', projectId)
  const { history } = useRouter()
  const [tab, setTab] = useTabSwitch('items')

  const [filters, setFilters] = useState<SubmittalsFilters>(EMPTY_SUBMITTALS_FILTERS)
  const [debouncedSearch] = useDebounce(filters.search, 300)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [lookupsDialogOpen, setLookupsDialogOpen] = useState(false)

  const [page, setPage] = useState(1)
  const pageSize = 25

  // Reset to page 1 whenever the filter set changes — NOT on tab change. All
  // three tabs render the SAME page slice (Items flat, Packages/Spec sections
  // grouping that same slice by design — see the pagination footer shared by
  // all three panes below), so a row seen on Items page 3 must still be in
  // Packages page 3's slice; resetting on tab switch would otherwise lock the
  // grouped tabs to page 1 forever since they carry no pager of their own to
  // recover from it.
  useEffect(() => {
    setPage(1)
  }, [filters])

  const hasActiveFilters =
    filters.status.length > 0 ||
    !!filters.specSectionId ||
    !!filters.categoryId ||
    !!filters.disciplineId ||
    !!filters.packageId ||
    !!filters.companyName ||
    !!filters.responsibleId ||
    !!filters.reviewerId ||
    !!filters.ballInCourtUserId ||
    !!filters.search.trim()

  // Only the free-text search is debounced (300ms) — Select-driven filters are
  // discrete clicks and should apply immediately, like every other filter bar.
  const serverFilter = useMemo(() => {
    const f: any = {}
    if (filters.status.length) f.status = filters.status
    if (filters.specSectionId) f.specSectionId = filters.specSectionId
    if (filters.categoryId) f.categoryId = filters.categoryId
    if (filters.disciplineId) f.disciplineId = filters.disciplineId
    if (filters.packageId) f.packageId = filters.packageId
    if (filters.companyName) f.companyName = filters.companyName
    if (filters.responsibleId) f.responsibleId = filters.responsibleId
    if (filters.reviewerId) f.reviewerId = filters.reviewerId
    if (filters.ballInCourtUserId) f.ballInCourtUserId = filters.ballInCourtUserId
    if (debouncedSearch.trim()) f.search = debouncedSearch.trim()
    return Object.keys(f).length ? f : undefined
  }, [filters, debouncedSearch])

  const { data, loading, error, refetch } = useQuery(Query.SUBMITTALS, {
    variables: { projectId, filter: serverFilter, limit: pageSize, offset: (page - 1) * pageSize },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  })

  const { data: lookupsData, refetch: refetchLookups } = useQuery(Query.SUBMITTAL_LOOKUP_VALUES, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: suggestionsData } = useQuery(Query.SUBMITTAL_COMPANY_SUGGESTIONS, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })

  const totalCount: number = data?.submittals?.totalCount || 0
  const items: any[] = data?.submittals?.items || []
  const lookups = lookupsData?.submittalLookupValues || []
  const companySuggestions: string[] = suggestionsData?.submittalCompanySuggestions || []
  const members = (membersData?.project?.users ?? []).map((m: any) => m.user).filter(Boolean)

  // SUBMITTALS returns { items, totalCount } (plain limit/offset), not a
  // connection with its own pageInfo — derive the same shape SnagsList gets
  // for free from SNAGS_CONNECTION so the pagination footer below is a direct
  // port of SnagsList's.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const hasPreviousPage = page > 1
  const hasNextPage = page < totalPages

  const packageGroups = useMemo(() => groupByLookup(items, 'package'), [items])
  const specSectionGroups = useMemo(() => groupByLookup(items, 'specSection'), [items])

  const handleRowClick = (row: { id: string }) => {
    history.push(`/project/${projectId}/submittals/${row.id}`)
  }

  const handleCreated = () => {
    refetch()
  }

  if (loading && !data) return <SubmittalsListSkeleton />

  if (error) {
    return (
      <div className="submittals-module">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold mb-0">{t('Submittals_title')}</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <AlertTriangle className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">
            {t('Submittals_error_loading')}: {error.message}
          </p>
        </div>
      </div>
    )
  }

  const toolbar = (
    <div className="py-2 flex items-center gap-3 flex-wrap">
      {/* Stats */}
      <div className="flex items-center gap-1">
        <span className="font-bold text-lg">{totalCount}</span>
        <span className="text-muted-foreground text-sm">{t('Submittals_total')}</span>
      </div>

      {/* Filters (grows to fill) */}
      <div className="flex-1 min-w-0">
        <SubmittalsFilterBar
          filters={filters}
          setFilters={setFilters}
          lookups={lookups}
          companySuggestions={companySuggestions}
          members={members}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {canAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setLookupsDialogOpen(true)}
            title={t('Submittals_lookups_title')}
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
        {canCreate && (
          <Button size="sm" className="h-8" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('Submittals_new_btn')}
          </Button>
        )}
      </div>
    </div>
  )

  // Pagination footer — direct port of SnagsList's (same component, same
  // page-window/ellipsis logic, same "Showing X-Y of Z" line); SUBMITTALS has
  // no connection-style pageInfo, so currentPage/totalPages/hasNext/hasPrevious
  // are derived above. Shared by all three tab panes (not just Items) — the
  // Packages/Spec sections panes group this SAME page slice by design, so they
  // need the same pager + range indicator to disclose that, rather than
  // silently showing only the newest 25 with no way to reach the rest.
  const renderPagination = () => {
    if (totalPages <= 1) return null
    return (
      <div className="px-3 py-2 border-t flex items-center justify-between">
        <div className="text-muted-foreground small">
          {t('Submittals_showing', {
            from: (page - 1) * pageSize + 1,
            to: Math.min(page * pageSize, totalCount),
            total: totalCount,
          })}
        </div>
        <Pagination size="sm" className="mb-0">
          <PaginationItem disabled={!hasPreviousPage}>
            <PaginationLink
              previous
              onClick={() => setPage(page - 1)}
              className={hasPreviousPage ? 'cursor-pointer' : 'cursor-not-allowed'}
            >
              <ChevronLeft className="h-4 w-4" />
            </PaginationLink>
          </PaginationItem>

          {/* First page */}
          {page > 2 && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(1)}>1</PaginationLink>
            </PaginationItem>
          )}

          {/* Ellipsis if needed */}
          {page > 3 && (
            <PaginationItem disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
          )}

          {/* Previous page */}
          {page > 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(page - 1)}>{page - 1}</PaginationLink>
            </PaginationItem>
          )}

          {/* Current page */}
          <PaginationItem active>
            <PaginationLink>{page}</PaginationLink>
          </PaginationItem>

          {/* Next page */}
          {page < totalPages && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(page + 1)}>{page + 1}</PaginationLink>
            </PaginationItem>
          )}

          {/* Ellipsis if needed */}
          {page < totalPages - 2 && (
            <PaginationItem disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
          )}

          {/* Last page */}
          {page < totalPages - 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => setPage(totalPages)}>{totalPages}</PaginationLink>
            </PaginationItem>
          )}

          <PaginationItem disabled={!hasNextPage}>
            <PaginationLink
              next
              onClick={() => setPage(page + 1)}
              className={hasNextPage ? 'cursor-pointer' : 'cursor-not-allowed'}
            >
              <ChevronRight className="h-4 w-4" />
            </PaginationLink>
          </PaginationItem>
        </Pagination>
      </div>
    )
  }

  const renderGroups = (groups: [string, any[]][]) => {
    if (groups.length === 0) {
      return (
        <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
          {t('Submittals_table_no_data')}
        </div>
      )
    }
    return (
      <div className="space-y-5">
        {groups.map(([label, groupItems]) => (
          <div key={label}>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground mb-2">
              {label}
              <Badge variant="secondary" className="font-normal">
                {groupItems.length}
              </Badge>
            </h3>
            <SubmittalsList items={groupItems} loading={loading} onRowClick={handleRowClick} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="submittals-module">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold mb-0">{t('Submittals_title')}</h1>
      </div>

      {totalCount === 0 && !hasActiveFilters ? (
        // Project genuinely has no submittals yet — tabs would all be equally
        // empty, so skip them and show one friendly empty state.
        <Card className="shadow-sm border-0">
          {toolbar}
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <ClipboardCheck className="h-12 w-12 mb-3 opacity-40" />
            <p className="text-sm">{t('Submittals_empty_hint')}</p>
          </div>
        </Card>
      ) : (
        <div className="tab">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={cn('flex items-center', { active: tab === 'items' })}
                onClick={() => setTab('items')}
              >
                <List className="h-4 w-4 mr-2" />
                {t('Submittals_tab_items')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={cn('flex items-center', { active: tab === 'packages' })}
                onClick={() => setTab('packages')}
              >
                <PackageIcon className="h-4 w-4 mr-2" />
                {t('Submittals_tab_packages')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={cn('flex items-center', { active: tab === 'specSections' })}
                onClick={() => setTab('specSections')}
              >
                <FileText className="h-4 w-4 mr-2" />
                {t('Submittals_tab_spec_sections')}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={tab}>
            <TabPane tabId="items">
              <Card className="shadow-sm border-0">
                {toolbar}
                <SubmittalsList items={items} loading={loading} onRowClick={handleRowClick} />
                {renderPagination()}
              </Card>
            </TabPane>
            <TabPane tabId="packages">
              <Card className="shadow-sm border-0">
                {toolbar}
                {renderGroups(packageGroups)}
                {renderPagination()}
              </Card>
            </TabPane>
            <TabPane tabId="specSections">
              <Card className="shadow-sm border-0">
                {toolbar}
                {renderGroups(specSectionGroups)}
                {renderPagination()}
              </Card>
            </TabPane>
          </TabContent>
        </div>
      )}

      {createDialogOpen && projectId && (
        <SubmittalCreateDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          projectId={projectId}
          onCreated={handleCreated}
        />
      )}

      {lookupsDialogOpen && projectId && (
        <SubmittalLookupsDialog
          open={lookupsDialogOpen}
          onOpenChange={setLookupsDialogOpen}
          projectId={projectId}
          onChanged={() => refetchLookups()}
        />
      )}
    </div>
  )
}

export default Submittals
